import React from "react";

// ─── FilterBadge ─────────────────────────────────────────────────────────────
// Renders the orange count bubble that sits on top of the FunnelIcon.
// Place inside the `relative` FunnelIcon wrapper div.
// Renders nothing when count is 0.

interface FilterBadgeProps {
  count: number;
}

export function FilterBadge({ count }: FilterBadgeProps) {
  if (count <= 0) return null;
  return (
    <span className="absolute -top-1.5 -right-2 flex items-center justify-center h-4 min-w-[1rem] rounded-full bg-entntorange text-white text-[9px] font-bold px-0.5 leading-none">
      {count}
    </span>
  );
}

// ─── FilterChips ──────────────────────────────────────────────────────────────
// Renders the "Active Filters" label and the pill list.
// Renders nothing when names is empty.

interface FilterChipsProps {
  names: (string | { label: string; onRemove?: () => void })[];
  /** Outer wrapper class — defaults to w-[90%] to match sidebar width */
  wrapperClassName?: string;
}

export function FilterChips({ names, wrapperClassName = "w-[90%]" }: FilterChipsProps) {
  if (names.length === 0) return null;
  return (
    <div className={`${wrapperClassName} mb-3 mt-1`}>
      <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
        Active Filters
      </p>
      <div className="flex flex-wrap gap-1.5">
        {names.map((item, idx) => {
          const isString = typeof item === "string";
          const label = isString ? item : item.label;
          const onRemove = isString ? undefined : item.onRemove;

          return (
            <span
              key={`${label}-${idx}`}
              className="inline-flex items-center gap-1 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200 rounded-full px-2.5 py-1 border border-orange-200 dark:border-orange-700 shadow-sm"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 dark:bg-orange-400 shrink-0" />
              {label}
              {onRemove && (
                <button
                  type="button"
                  onClick={onRemove}
                  className="ml-0.5 font-bold hover:text-orange-600 dark:hover:text-orange-300"
                  title={`Remove ${label}`}
                >
                  ✕
                </button>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
